import { useCallback } from 'react'

import isEmpty from 'lodash/isEmpty'
import isEqual from 'lodash/isEqual'

import { DIALOG_TYPE } from '@/dialogs/constants'
import { openDialog } from '@/models/dialogs'

import { useSalesEditor } from './hooks/use-sales-editor'
import { type SalesEditorTemplate } from './state/types'
import { mergeChangesWithTemplate } from './utils'

export const useSalesTemplateActions = () => {
  const {
    activeTemplate,
    changeActiveTemplate,
    currentChanges,
    setCurrentChanges,
    createNewTemplate,
  } = useSalesEditor()
  const handleChangeActiveTemplate = useCallback(
    (id?: string, discardUnsavedChanges?: boolean) => {
      if (discardUnsavedChanges) {
        setCurrentChanges({})
        changeActiveTemplate(id)

        return
      }
      if (isEmpty(currentChanges)) {
        changeActiveTemplate(id)

        return
      }

      const onSaveChangesToNewTemplate = () => {
        if (!activeTemplate) return
        const newTemplateData = mergeChangesWithTemplate(
          activeTemplate.data,
          currentChanges,
        )

        const onSaveNew = (name: string) => {
          const template = createNewTemplate(name, newTemplateData)

          changeActiveTemplate(template.id)
          setCurrentChanges({})
        }

        openDialog({
          type: DIALOG_TYPE.CREATE_SALES_TEMPLATE,
          onSave: onSaveNew,
        })
      }

      openDialog({
        type: DIALOG_TYPE.UNSAVED_SALES_TEMPLATE,
        onReset: () => {
          handleChangeActiveTemplate(id, true)
        },
        onSave: onSaveChangesToNewTemplate,
      })
    },
    [
      createNewTemplate,
      changeActiveTemplate,
      activeTemplate,
      currentChanges,
      setCurrentChanges,
    ],
  )

  const handleClickButton =
    (handler: () => void): React.MouseEventHandler<HTMLButtonElement> =>
    (event) => {
      event.stopPropagation()
      handler()
    }

  const handleClickEdit = (template: SalesEditorTemplate) =>
    handleClickButton(() => {
      openDialog({
        type: DIALOG_TYPE.EDIT_SALES_TEMPLATE,
        template,
      })
    })

  const handleClickDelete = useCallback(
    (template: SalesEditorTemplate) =>
      handleClickButton(() => {
        openDialog({
          type: DIALOG_TYPE.DELETE_SALES_TEMPLATE,
          template,
        })
      }),
    [],
  )

  const handleClickCopy = useCallback(() => {
    if (!activeTemplate) return
    const newTemplateData = mergeChangesWithTemplate(
      activeTemplate.data,
      currentChanges,
    )

    openDialog({
      type: DIALOG_TYPE.CREATE_SALES_TEMPLATE,
      data: newTemplateData,
      onSave: (name) => {
        const template = createNewTemplate(name, newTemplateData)

        handleChangeActiveTemplate(template.id, true)
      },
    })
  }, [
    activeTemplate,
    currentChanges,
    createNewTemplate,
    handleChangeActiveTemplate,
  ])

  const handleClickNew = useCallback(() => {
    openDialog({
      type: DIALOG_TYPE.CREATE_SALES_TEMPLATE,
      onSave: (name) => {
        const template = createNewTemplate(name)

        handleChangeActiveTemplate(
          template.id,
          isEqual(currentChanges, template.data),
        )
      },
    })
  }, [createNewTemplate, handleChangeActiveTemplate, currentChanges])

  return {
    onEdit: handleClickEdit,
    onDelete: handleClickDelete,
    onCopy: handleClickCopy,
    onCreate: handleClickNew,
    onSelectTemplate: handleChangeActiveTemplate,
  }
}
